import { Component, Injector, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AlertsService } from '../../../base/services/alerts.service';
import { BaseService } from 'src/modules/base/shared/services/base.service';
import { LoaderService } from 'src/modules/base/services/loader.service';
import { FormService } from 'src/modules/base/components/form/form.service';
import { QuestionControlService } from 'src/modules/base/components/form/question-control.service';
import { FacilityUserOnboardingContextService } from './facility-user-onboarding-context.service';

@Component({
  selector: 'app-facility-user-onboarding',
  templateUrl: './facility-user-onboarding.component.html',
  styleUrls: ['./facility-user-onboarding.component.scss'],
  standalone: false,
  providers: [FormService, QuestionControlService]
})

export class FacilityUserOnboardingComponent implements OnInit {
  @Input() showAsTable: boolean;
  resource_name: any;
  _currentResource: string;
  minRows: number = 1;
  allFields: any[] = [];
  fields: any[] = [];
  rowQuestions: any[][] = [];
  usersForm: FormGroup;
  submitted: boolean = false;
  facility_id: any
  checklist: any
  decorator: any = false;
  // The MasterRow HospitalOnboardComponent.startUpload() opened this popup
  dataComingFromHospitalOnboardingComponent: any;
  constructor(
    private fb: FormBuilder,
    public alerts: AlertsService,
    private baseService: BaseService,
    private loader: LoaderService,
    private formService: FormService,
    private onboardingContext: FacilityUserOnboardingContextService,
    private route: ActivatedRoute,
    private injector: Injector,
  ) { }

  ngOnInit(): void {
    this.dataComingFromHospitalOnboardingComponent = this.onboardingContext.getRow();
    this.facility_id = sessionStorage.getItem('facility_id') ?? '';
    // row.checklist is the same master_upload_checklist record
    // GET_CHECKLIST_FOR_TEMPLATE used to fetch here - HospitalOnboardComponent
    // already has it (see MasterRow.checklist), so no need to refetch it.
    this.checklist = this.dataComingFromHospitalOnboardingComponent?.checklist ? [this.dataComingFromHospitalOnboardingComponent.checklist] : [];
    this.usersForm = this.fb.group({
      users: this.fb.array([])
    });
    this._currentResource = this['resource'].resource ?? "";
    this.getResourceDef();
  }

  getResourceDef() {
    this.loader.showSpinner()
    this.baseService.getResourceDef(this._currentResource, (resouceDef) => {
      if (resouceDef) {
        this.loader.hideSpinner()
        this.resource_name = resouceDef;
        if (resouceDef.config != undefined && resouceDef.config.decorator != undefined) {
          this.decorator = this.baseService.initDecorator(resouceDef.config.decorator, this.route, this.injector);
        }
        this.allFields = this.resource_name?.list?.fields || [];
        this.formService.setEditableGrid(true);
        this.fields = this.allFields.filter(field => field.component !== 'hidden');
        if (this.showAsTable === undefined || this.showAsTable === null) {
          this.showAsTable = !!this.resource_name.currentAction;
        }
        const roleField = this.allFields.find(field => field.name === 'role');
        if (this.decorator?.filterRoleOptions && roleField) {
          this.decorator.filterRoleOptions(this.facility_id, roleField, () => {
            for (let i = 0; i < this.minRows; i++) {
              this.addRow();
            }
          });
        } else {
          for (let i = 0; i < this.minRows; i++) {
            this.addRow();
          }
        }
      }
    });
    this.loader.hideSpinner()
  }


  get rows(): FormArray {
    return this.usersForm.get('users') as FormArray;
  }

  buildRow(): FormGroup {
    const clonedFields = JSON.parse(JSON.stringify(this.allFields));
    const questions = this.formService.buildQuestions(clonedFields, null, { data: { facility_id: this.facility_id } });
    const group: any = {};
    questions.forEach(question => {
      const validators = question.required ? [Validators.required] : [];

      const control = new FormControl(question.value || '', validators);
      // pre-seed a question_component stub so dependents dispatch (onValueChanged)
      // can read control.question_component.question before dhs-form-question mounts
      control['question_component'] = { question };
      group[question.key] = control;
      // set facility Id
      if (question.key == "facility_id") {
        control.setValue(this.facility_id)
      }
    });
    this.rowQuestions.push(questions);
    return this.fb.group(group);
  }

  addRow(): void {
    this.rows.push(this.buildRow());
  }

  removeRow(index: number): void {
    if (this.rows.length > 1) {
      this.rows.removeAt(index);
      this.rowQuestions.splice(index, 1);
    }
  }

  copyRow(index: number): void {
    const source = this.rows.at(index) as FormGroup;
    const clonedFields = JSON.parse(JSON.stringify(this.allFields));
    const questions = this.formService.buildQuestions(clonedFields, null, { data: { facility_id: this.facility_id } });
    const group: any = {};
    questions.forEach(question => {
      const validators = question.required ? [Validators.required] : [];
      const value = question.key === 'login_id' ? '' : (source.get(question.key)?.value ?? '');
      const control = new FormControl(value, validators);
      control['question_component'] = { question };
      group[question.key] = control;
    });
    this.rows.insert(index + 1, this.fb.group(group));
    this.rowQuestions.splice(index + 1, 0, questions);
  }

  onValueChanged(event: any, row: FormGroup): void {
    if (row?.controls?.display_name && (event.question?.key === 'first_name' || event.question?.key === 'middle_name' || event.question?.key === 'last_name')) {
      const firstName = row.value?.first_name || '';
      const middleName = row.value?.middle_name || '';
      const lastName = row.value?.last_name || '';
      const displayName = `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim();
      row?.controls?.display_name?.setValue(displayName);
    }
    const question = event?.question;
    if (!question || !question.dependents || !question.dependents.length) {
      return;
    }
    question.dependents.forEach(dependents => {
      switch (dependents.action) {
        case 'change_value':
          this.changeValue(row, dependents);
          break;
        case 'populate_field':
          this.populateField(row, dependents, event.val);
          break;
        default:
          break;
      }
    });
  }

  changeValue(row: FormGroup, dependents: any): void {
    const dependentControl = row.controls[dependents.dependent_field];
    if (!dependentControl || !dependentControl['question_component']) {
      return;
    }
    const question = dependentControl['question_component'].question;
    const cloneDependent = JSON.parse(JSON.stringify(dependents));
    cloneDependent.args = cloneDependent.args
      ? this.baseService.buildDyanamicStr(cloneDependent.args, { data: row.getRawValue() })
      : '';
    cloneDependent.filter = cloneDependent.filter
      ? this.baseService.buildDyanamicStr(cloneDependent.filter, { data: row.getRawValue() })
      : '';
    const field: any = {
      datasource: cloneDependent,
      getterKey: question.getterKey,
      getterValue: question.getterValue,
      req_keys: question.req_keys,
    };
    this.formService.setdropdownOptions(field, question, this);
  }

  populateField(row: FormGroup, dependents: any, currentVal: any): void {
    const dependentControl = row.controls[dependents.dependent_field];
    if (!dependentControl) {
      return;
    }
    if (currentVal && typeof currentVal === 'object' && currentVal[dependents.value] !== undefined) {
      dependentControl.setValue(currentVal[dependents.value]);
    }
  }

  requiredTotal(): number {
    return this.fields.filter(f => f.required).length;
  }

  requiredFilled(row: FormGroup): number {
    return this.fields
      .filter(f => f.required)
      .filter(f => (row.get(f.name).value || '').toString().trim().length > 0)
      .length;
  }

  rowProgress(row: FormGroup): number {
    const total = this.requiredTotal();
    if (total === 0) {
      return 100;
    }
    return Math.round((this.requiredFilled(row) / total) * 100);
  }

  isRowReady(row: FormGroup): boolean {
    return this.rowProgress(row) === 100;
  }

  readyCount(): number {
    return this.rows.controls.filter((row: FormGroup) => this.isRowReady(row)).length;
  }

  showError(row: FormGroup, field: any): boolean {
    const control = row.get(field.key || field.name);
    // searchselect (kx-multiselect-dropdown) marks its control touched on its
    // own init render, before any real user interaction - so for that
    // component only, rely on submitted rather than touched/dirty.
    if (field.controlType === 'searchselect') {
      return control.invalid && this.submitted;
    }
    return control.invalid && (this.submitted || control.touched || control.dirty);
  }

  onSubmit(): void {
    this.submitted = true;
    this.rows.controls.forEach((row: FormGroup) => {
      this.fields.forEach(f => row.get(f.name).markAsTouched());
    });
    this.loader.showSpinner()
    if (this.usersForm.valid) {
      const datasource = this.resource_name.buttons[0].datasource || {};
      if (datasource) {
        const payload = this.usersForm.value.users.map((user: any) => ({ ...user, facility_id: this.facility_id, is_smb_upload: true, smb_upload_master: this.getMasterName() }));
        this.baseService.action(payload, datasource.action, datasource.application, datasource.resource).subscribe((res: any) => {
          if (res != undefined && res.errCode == 0) {
            this.alerts.success('Request Successful');
            this.dataSenduploadCheckListCollection(payload.length)
            this.resetForm();
            this.loader.hideSpinner()
          } else {
            this.alerts.error(res.message);
            this.loader.hideSpinner()
          }
        });
      } else {
        this.alerts.error("DataSource Error")
        this.loader.hideSpinner()
      }
    } else {
      this.alerts.error('Complete all required fields before submitting');
      this.loader.hideSpinner()
    }
  }

  resetForm(): void {
    this.rows.clear();
    this.rowQuestions = [];
    this.submitted = false;
    for (let i = 0; i < this.minRows; i++) {
      this.addRow();
    }
  }

  dataSenduploadCheckListCollection(recordsLength: number) {
    const payload = {
      ...this.checklist[0],
      facility_id: this.facility_id,
      status_code: "executed",
      status_name: "Executed",
      latest_upload_status: {
        ...this.checklist[0].latest_upload_status,
        total_records: recordsLength,
        successful_records: recordsLength,
        failed_records: 0,
        execution_time: Date.now(),
      },
    };
    this.baseService.action(payload, 'modify', 'data-onboarding', 'master_upload_checklist').subscribe((res: any) => {
      if (res != undefined && res.errCode == 0) {
        console.log("Data Added in Upload checkList Master")
      } else {
        console.log("Error ! Data Not Added in Upload checkList Master")
      }
    });
  }

  getMasterName() {
    return this._currentResource == "facility_master_digital_on_boarding" ? "Facility Admin" : "Practitioner";
  }
}
